import { useUpdateEffect } from 'react-use';
import { useMemo } from 'react';

import { MedicineSimple } from '@dreryk/drerykweb-medicine-api';

import {
   AdditionalRequirementType,
   ExtendedDosage,
   getExceededTreatmentDaysCountMaxValue,
   getTreatmentDaysCountMaxValue,
   hasDailyDose,
   hasKnownPackageCapacity,
   hasNotAllowedDosageFrequency,
   isDosageExceedingPackages,
   isPreciseDosageFrequencyAllowed,
   isPreciseDosageFrequencyValidated,
   isTreatmentDaysCountExceeded,
   isTreatmentDaysCountExceedingYearLimit,
   isUnderTreatmentSupervision,
   Reimbursement,
   useMedicineSufficiencyCount,
} from '~libs.shared.prescription';

import { AdditionalRequirement } from '../../prescription/additionalRequirements/additionalRequirements.types';
import { useTreatmentDaysCountSync } from '../../prescription/treatmentDaysCount/TreatmentDaysCount.hooks';
import { DraftPrescriptionsFormController } from '../DraftPrescriptionsForm.types';

import { createDraftStandardPrescriptionsFieldNameFactory } from './DraftStandardPrescriptionField.utils';

type UseSynchronizeDosageValidationOptions = Pick<DraftPrescriptionsFormController, 'watch' | 'trigger'> & {
   index: number;
   underTreatmentSupervision: boolean;
   extendedDosageActive: boolean;
};

type DosageFieldRequired = boolean;

export const useDosageValidationSync = (options: UseSynchronizeDosageValidationOptions): DosageFieldRequired => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(options.index);

   const numericalDosageFrequencyFieldName = createFieldName('numericalDosageFrequency');
   const numericalDosageQuantityFieldName = createFieldName('numericalDosageQuantity');

   const currentNumericalDosageFrequency = options.watch(numericalDosageFrequencyFieldName);
   const currentNumericalDosageQuantity = options.watch(numericalDosageQuantityFieldName);
   const dosageFieldRequired =
      !options.extendedDosageActive &&
      (!currentNumericalDosageFrequency || !currentNumericalDosageQuantity) &&
      !options.underTreatmentSupervision;

   useUpdateEffect(() => {
      if (!dosageFieldRequired) {
         options.trigger(createFieldName('dosage'));
      }
   }, [dosageFieldRequired]);

   return dosageFieldRequired;
};

type UseSynchronizeNumericalDosageValidationOptions = Pick<DraftPrescriptionsFormController, 'trigger'> & {
   index: number;
   underTreatmentSupervision: boolean;
};

export const useNumericalDosageValidationSync = (options: UseSynchronizeNumericalDosageValidationOptions): void => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(options.index);

   useUpdateEffect(() => {
      if (options.underTreatmentSupervision) {
         return;
      }

      options.trigger([
         createFieldName('numericalDosageFrequency'),
         createFieldName('numericalDosageQuantity'),
         createFieldName('dosageUnit'),
         createFieldName('treatmentDaysCount'),
      ]);

      // eslint-disable-next-line react-hooks/exhaustive-deps
   }, [options.underTreatmentSupervision]);
};

type UseDosageFrequencyNotAllowedOptions = Pick<DraftPrescriptionsFormController, 'watch'> & {
   index: number;
   issuedForYear: boolean;
   reimbursement: Reimbursement | null | undefined;
   extendedDosage: ExtendedDosage | null;
};

export type DosageFrequencyState = {
   validated: boolean;
   notAllowed: boolean;
};

export const useDosageFrequencyNotAllowed = (options: UseDosageFrequencyNotAllowedOptions): DosageFrequencyState => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(options.index);
   const currentFrequency: number | null = options.watch(createFieldName('numericalDosageFrequency'));

   const validated = isPreciseDosageFrequencyValidated({
      issuedForYear: options.issuedForYear,
      reimbursement: options.reimbursement,
   });

   const notAllowed = options.extendedDosage?.stages.length
      ? hasNotAllowedDosageFrequency(options.extendedDosage.stages)
      : !isPreciseDosageFrequencyAllowed(currentFrequency);

   return { validated: validated, notAllowed: validated && notAllowed };
};

type UseTreatmentDaysCountStateOptions = Pick<DraftPrescriptionsFormController, 'watch' | 'setValue'> & {
   index: number;
   medicine: MedicineSimple;
   issuedForYear: boolean;
   extendedDosageActive: boolean;
   reimbursement: Reimbursement | null | undefined;
};

export type TreatmentDaysCountState = {
   underTreatmentSupervision: boolean;
   medicineSufficiencyCount: number | undefined;
   maxValue: number;
   exceeded: boolean;
   exceededMaxValue: number;
   changeToPrescriptionForYearAvailable: boolean;
   dosageExceedingPackages: boolean;
   editable: boolean;
};

export const useTreatmentDaysCountState = (options: UseTreatmentDaysCountStateOptions): TreatmentDaysCountState => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(options.index);

   const currentQuantity: number | null = options.watch(createFieldName('quantity'));
   const currentNumericalDosageFrequency: number | null = options.watch(createFieldName('numericalDosageFrequency'));
   const currentNumericalDosageQuantity: number | null = options.watch(createFieldName('numericalDosageQuantity'));
   const currentDosageUnit: string | null = options.watch(createFieldName('dosageUnit'));
   const currentTreatmentDaysCount: number | null | undefined = options.watch(createFieldName('treatmentDaysCount'));

   const preciseDosage = useMemo(
      () => ({
         frequency: currentNumericalDosageFrequency,
         portion: currentNumericalDosageQuantity,
         unit: currentDosageUnit,
      }),
      [currentNumericalDosageFrequency, currentNumericalDosageQuantity, currentDosageUnit]
   );

   const medicineSufficiencyCount = useMedicineSufficiencyCount({
      medicine: options.medicine,
      preciseDosage: preciseDosage,
      packagesCount: currentQuantity,
   });

   const editable = hasDailyDose(preciseDosage);

   useTreatmentDaysCountSync({
      setValue: options.setValue,
      medicine: options.medicine,
      preciseDosage: preciseDosage,
      medicineSufficiencyCount: medicineSufficiencyCount,
      treatmentDaysCountFieldName: createFieldName('treatmentDaysCount'),
      packagesCountFieldName: createFieldName('quantity'),
      currentTreatmentDaysCount: currentTreatmentDaysCount,
      currentPackagesCount: currentQuantity,
      editable: editable,
      enabled: !options.extendedDosageActive,
   });

   const supervisionOptions = {
      issuedForYear: options.issuedForYear,
      reimbursement: options.reimbursement,
      packageCapacityKnown: hasKnownPackageCapacity(options.medicine),
   };

   const underTreatmentSupervision = isUnderTreatmentSupervision(supervisionOptions);

   const exceededOptions = {
      issuedForYear: options.issuedForYear,
      treatmentDaysCount: currentTreatmentDaysCount,
      medicineSufficiencyCount: medicineSufficiencyCount,
      reimbursement: options.reimbursement,
      packagesCount: currentQuantity,
   };

   return {
      underTreatmentSupervision: underTreatmentSupervision,
      medicineSufficiencyCount: medicineSufficiencyCount,
      maxValue: getTreatmentDaysCountMaxValue(supervisionOptions),
      exceeded: isTreatmentDaysCountExceeded(exceededOptions),
      exceededMaxValue: getExceededTreatmentDaysCountMaxValue(exceededOptions),
      changeToPrescriptionForYearAvailable:
         !options.issuedForYear && !isTreatmentDaysCountExceedingYearLimit(exceededOptions),
      dosageExceedingPackages: isDosageExceedingPackages({
         medicine: options.medicine,
         preciseDosage: preciseDosage,
         packagesCount: currentQuantity,
      }),
      editable: editable,
   };
};

type IssuedForYear = boolean;
type UseIssuedForYearOptions = Pick<DraftPrescriptionsFormController, 'watch'> & {
   index: number;
   additionalRequirementsTypes: AdditionalRequirementType[] | undefined;
};

export const useIssuedForYear = (options: UseIssuedForYearOptions): IssuedForYear => {
   const createFieldName = createDraftStandardPrescriptionsFieldNameFactory(options.index);

   const initialAdditionalRequirements = options.additionalRequirementsTypes;
   // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
   const currentAdditionalRequirements = options.watch(createFieldName('additionalRequirements')) as
      | AdditionalRequirement[]
      | null;
   return Boolean(
      currentAdditionalRequirements
         ? currentAdditionalRequirements.some((requirement) => requirement.type === 'FOR_YEAR')
         : initialAdditionalRequirements?.includes('FOR_YEAR')
   );
};
